import { createSelector } from '@reduxjs/toolkit';
import orderBy from 'lodash-es/orderBy';

import { AgentActivity } from '@/shared/types/rtm';
import { RootState } from '@/shared/types/state';

/**
 * Selector to retrieve viewers by brain ID.
 * @param {RootState} state - The global state of the application.
 * @param {string} brainId - The unique identifier for the brain.
 * @returns {Array} An array of viewers associated with the specified brain ID.
 */
export const selectViewersByBrainId = (state: RootState, brainId: string) =>
  state.presence.brains?.[brainId];

/**
 * Selector to retrieve viewers by collection ID.
 * @param {RootState} state - The global state of the application.
 * @param {string} collectionId - The unique identifier for the collection.
 * @returns {Array} An array of viewers associated with the specified collection ID.
 */
export const selectViewersByCollectionId = (state: RootState, collectionId: string) =>
  state.presence.collections?.[collectionId];

export const selectViewersByGuidelinesId = (state: RootState, brainId: string) =>
  state.presence.guidelines?.[brainId];

/**
 * Selector to return all the account members.
 * @param {RootState} state - The global state of the application.
 * @returns {Array} An array of all agents.
 */
export const selectAllAgents = (state: RootState) => state.presence.agents;

/**
 * Selector to retrieve an agent by their ID.
 * @param {RootState} state - The global state of the application.
 * @param {string} agentId - The unique identifier for the agent.
 * @returns {Object|null} The agent object if found, otherwise null.
 */
export const selectAgentById = createSelector(
  [selectAllAgents, (_, agentId) => agentId],
  (agents, agentId) => {
    if (!agentId) {
      return null;
    }

    return agents.find((agent) => agent.agent_id === agentId);
  }
);

const selectOnlineAgents = createSelector([selectAllAgents], (agents) =>
  agents.filter((agent) => agent.status === 'online')
);

/**
 * Selector to retrieve viewers by session ID.
 * @param {RootState} state - The global state of the application.
 * @param {string} sessionId - The unique identifier for the session.
 * @returns {Array} An array of viewers associated with the specified session ID.
 */
export const selectViewersBySessionId = (state: RootState, sessionId: string) =>
  state.presence.sessions[sessionId];

/**
 * Selector to get online agent IDs only.
 * Separated to prevent activity updates from affecting viewer calculations.
 */
export const selectOnlineAgentIds = createSelector([selectAllAgents], (agents) => {
  return new Set(agents.filter((agent) => agent.status === 'online').map((a) => a.agent_id));
});

const EMPTY_VIEWERS: { viewers: AgentActivity[]; overflow: AgentActivity[]; count: number } =
  Object.freeze({ viewers: [], overflow: [], count: 0 });

/**
 * Selector to retrieve the last three online viewers by session ID, excluding the current user.
 * @param {RootState} state - The global state of the application.
 * @param {string} sessionId - The unique identifier for the session.
 * @param {string} [currentUserId] - The current user, excluded from viewers, overflow and count.
 * @returns {Object} An object containing up to three other online viewers, the remaining ones, and the total count of other online viewers.
 */
export const selectLast3ViewersBySessionId = createSelector(
  [
    selectOnlineAgentIds,
    selectViewersBySessionId,
    (_: RootState, __: string, currentUserId?: string) => currentUserId,
  ],
  (onlineAgentIds, viewers, currentUserId) => {
    const otherViewers = viewers?.filter(
      (viewer) => onlineAgentIds.has(viewer.user_id) && viewer.user_id !== currentUserId
    );

    if (!otherViewers?.length) {
      return EMPTY_VIEWERS;
    }

    const sortedViewers = [...otherViewers].sort((a, b) => {
      const timeDiff = a.timestamp - b.timestamp;
      if (timeDiff !== 0) {
        return timeDiff;
      }
      return a.user_id.localeCompare(b.user_id);
    });

    return {
      viewers: sortedViewers.slice(0, 3),
      overflow: sortedViewers.slice(3),
      count: sortedViewers.length,
    };
  }
);

export const selectOnlineViewersByBrainId = createSelector(
  [selectOnlineAgents, selectViewersByBrainId],
  (onlineAgents, viewers) => {
    const onlineViewers = viewers?.filter((viewer) => {
      return onlineAgents.some((agent) => agent.agent_id === viewer.user_id);
    });
    return { viewers: orderBy(onlineViewers, ['last_activity_at'], ['desc']) };
  }
);

export const selectOnlineViewersByGuidelinesId = createSelector(
  [selectOnlineAgents, selectViewersByGuidelinesId],
  (onlineAgents, viewers) => {
    const onlineViewers = viewers?.filter((viewer) => {
      return onlineAgents.some((agent) => agent.agent_id === viewer.user_id);
    });
    return { viewers: orderBy(onlineViewers, ['last_activity_at'], ['desc']) };
  }
);

/**
 * Selector to return a map of agent IDs to agent names.
 * Optimized for components that only need to look up agent names.
 * This selector uses shallow comparison to prevent unnecessary updates
 * when agent activity changes but names remain the same.
 * @param {RootState} state - The global state of the application.
 * @returns {Object} A map of agent_id to name.
 */
export const selectAgentNamesMap = createSelector([selectAllAgents], (agents) => {
  const namesMap: Record<string, string> = {};
  agents.forEach((agent) => {
    namesMap[agent.agent_id] = agent.name;
  });
  return namesMap;
});

/**
 * Optimized selector for getting a single agent's name.
 * This prevents components from rerendering when unrelated agents update.
 * @param {RootState} state - The global state of the application.
 * @param {string} agentId - The agent ID to get the name for.
 * @returns {string | undefined} The agent's name or undefined if not found.
 */
export const selectAgentNameById = createSelector(
  [selectAllAgents, (_, agentId: string) => agentId],
  (agents, agentId) => {
    if (!agentId) {
      return undefined;
    }
    const agent = agents.find((a) => a.agent_id === agentId);
    return agent?.name;
  }
);

/**
 * Selector to return agents with only essential fields (id, name, status).
 * Optimized for components that need to display agent lists with status.
 * @param {RootState} state - The global state of the application.
 * @returns {Array} An array of agents with only id, name, and status fields.
 */
export const selectAgentsWithStatus = createSelector([selectAllAgents], (agents) => {
  return agents.map((agent) => ({
    agent_id: agent.agent_id,
    name: agent.name,
    status: agent.status,
  }));
});

/**
 * Selector to return a map of agent IDs to their status.
 * Optimized for quick status lookups without triggering rerenders on other field changes.
 * @param {RootState} state - The global state of the application.
 * @returns {Object} A map of agent_id to status.
 */
export const selectAgentStatusMap = createSelector([selectAllAgents], (agents) => {
  const statusMap: Record<string, string> = {};
  agents.forEach((agent) => {
    statusMap[agent.agent_id] = agent.status;
  });
  return statusMap;
});
